<template>
  <div class="content-wrapper-inner">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title"><i class="fa fa-gift"></i> Asignar Bonificación Quincenal</h3>
      </div>
      <div class="box-body">
        <!-- Filtros -->
        <el-form :inline="true" size="small" class="demo-form-inline">
          <el-form-item label="Área Funcional">
            <el-select v-model="filters.functional_area_id" placeholder="Todas las áreas" clearable filterable style="width: 220px;">
              <el-option
                v-for="item in functionalAreas"
                :key="item.id"
                :label="item.description"
                :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Nómina">
            <el-select v-model="filters.roster_id" placeholder="Todas las nóminas" clearable filterable style="width: 180px;">
              <el-option
                v-for="item in rosters"
                :key="item.id"
                :label="item.description"
                :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Mes/Año">
            <el-date-picker
              v-model="filters.selected_month"
              type="month"
              format="yyyy-MM"
              value-format="yyyy-MM"
              placeholder="Seleccionar mes"
              style="width: 160px;">
            </el-date-picker>
          </el-form-item>

          <el-form-item label="Período Quincenal">
            <el-radio-group v-model="filters.fortnight" size="small">
              <el-radio-button label="1">1° Quincena (1 - 15)</el-radio-button>
              <el-radio-button label="2">2° Quincena (16 - Fin de mes)</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="Buscar">
            <el-input
              v-model="filters.filter"
              placeholder="Nombre o Cédula"
              clearable
              @keyup.enter.native="loadEmployees"
              style="width: 180px;">
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="loadEmployees" :loading="loading">
              Consultar
            </el-button>
          </el-form-item>
        </el-form>

        <!-- Banner destacado de Período Seleccionado y Fecha Límite de Corte -->
        <div v-if="dateRangeText" class="period-info-banner">
          <div class="period-info-left">
            <div class="period-label"><i class="fa fa-calendar-check-o"></i> PERÍODO SELECCIONADO</div>
            <div class="period-dates">{{ dateRangeText }}</div>
          </div>

          <div class="period-info-right" v-if="cutoffInfo && cutoffInfo.enabled">
            <div class="cutoff-badge" :class="{ 'cutoff-expired-badge': cutoffExpired }">
              <i class="fa" :class="cutoffExpired ? 'fa-lock' : 'fa-clock-o'"></i>
              <span>Fecha Límite de Aprobación: <strong>{{ cutoffInfo.deadline_formatted }} (Día {{ cutoffInfo.active_limit_day }})</strong></span>
            </div>
          </div>
          <div class="period-info-right" v-else-if="cutoffInfo && !cutoffInfo.enabled">
            <span class="label label-default" style="font-size: 13px; padding: 8px 12px; border-radius: 6px;">
              <i class="fa fa-info-circle"></i> Corte General: Deshabilitado
            </span>
          </div>
        </div>

        <el-alert
          v-if="cutoffExpired"
          :title="cutoffMessage || 'La fecha de corte general para este período ha expirado. No se permiten asignaciones o modificaciones de bonificaciones.'"
          type="error"
          show-icon
          :closable="false"
          style="margin-bottom: 15px;">
        </el-alert>

        <!-- Acciones en lote -->
        <div v-if="employees.length > 0" style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center;">
          <span style="font-weight: bold; color: #555;">Total Empleados: {{ employees.length }}</span>
          <el-button type="success" icon="el-icon-check" @click="saveBonuses" :loading="saving" :disabled="cutoffExpired">
            Guardar Bonificaciones
          </el-button>
        </div>

        <!-- Tabla de Empleados -->
        <el-table
          :data="employees"
          stripe
          border
          v-loading="loading"
          style="width: 100%"
          max-height="600">
          <el-table-column prop="company_id_number" label="Cédula / Código" width="140" sortable></el-table-column>

          <el-table-column label="Empleado" min-width="220" sortable prop="employee_name">
            <template slot-scope="scope">
              <strong>{{ scope.row.employee_name || ((scope.row.last_name ? scope.row.last_name + ', ' : '') + scope.row.first_name) }}</strong>
            </template>
          </el-table-column>

          <el-table-column prop="functional_area_description" label="Área" width="160" sortable></el-table-column>

          <el-table-column prop="roster_description" label="Nómina" width="150"></el-table-column>

          <el-table-column label="Valor Bonificación ($)" width="220">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.amount"
                :min="0"
                :step="50000"
                controls-position="right"
                size="small"
                :disabled="!canEditBonus(scope.row)"
                style="width: 100%;">
              </el-input-number>
            </template>
          </el-table-column>

          <el-table-column label="Observación">
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.observation"
                placeholder="Observación o motivo de la bonificación..."
                size="small"
                :disabled="!canEditBonus(scope.row)">
              </el-input>
            </template>
          </el-table-column>

          <el-table-column label="Estado" width="130" align="center">
            <template slot-scope="scope">
              <el-tag v-if="cutoffExpired" type="danger" size="small">
                <i class="el-icon-lock"></i> Corte Vencido
              </el-tag>
              <el-tag v-else-if="scope.row.bonus_id" type="success" size="small">
                <i class="el-icon-lock"></i> Guardado
              </el-tag>
              <el-tag v-else type="info" size="small">
                Pendiente
              </el-tag>
            </template>
          </el-table-column>
        </el-table>

        <!-- Botón Guardar inferior -->
        <div v-if="employees.length > 0" style="margin-top: 15px; text-align: right;">
          <el-button type="success" icon="el-icon-check" @click="saveBonuses" :loading="saving" :disabled="cutoffExpired">
            Guardar Bonificaciones
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AssignBonuses',
  data() {
    return {
      loading: false,
      saving: false,
      cutoffExpired: false,
      cutoffMessage: '',
      cutoffInfo: null,
      functionalAreas: [],
      rosters: [],
      filters: {
        functional_area_id: null,
        roster_id: null,
        selected_month: moment().format('YYYY-MM'),
        fortnight: moment().date() <= 15 ? '1' : '2',
        filter: ''
      },
      employees: [],
      computedDates: {
        date_from: null,
        date_to: null
      }
    };
  },
  computed: {
    dateRangeText() {
      if (!this.computedDates.date_from || !this.computedDates.date_to) return '';
      return `${this.computedDates.date_from} al ${this.computedDates.date_to}`;
    }
  },
  mounted() {
    this.loadOptions();
    this.updateComputedDates();
    this.loadEmployees();
  },
  watch: {
    'filters.selected_month'() {
      this.updateComputedDates();
      this.loadEmployees();
    },
    'filters.fortnight'() {
      this.updateComputedDates();
      this.loadEmployees();
    },
    'filters.functional_area_id'() {
      this.loadEmployees();
    },
    'filters.roster_id'() {
      this.loadEmployees();
    }
  },
  methods: {
    updateComputedDates() {
      if (!this.filters.selected_month) return;
      const yearMonth = this.filters.selected_month;
      if (String(this.filters.fortnight) === '1') {
        this.computedDates.date_from = `${yearMonth}-01`;
        this.computedDates.date_to = `${yearMonth}-15`;
      } else {
        const lastDay = moment(yearMonth, 'YYYY-MM').endOf('month').format('DD');
        this.computedDates.date_from = `${yearMonth}-16`;
        this.computedDates.date_to = `${yearMonth}-${lastDay}`;
      }
    },
    loadOptions() {
      axios.get('/api/functionalareas/optionsByUser').then(response => {
        const data = response.data;
        if (Array.isArray(data)) {
          this.functionalAreas = data;
        } else if (typeof data === 'object' && data !== null) {
          this.functionalAreas = Object.keys(data).map(id => ({
            id: isNaN(parseInt(id)) ? id : parseInt(id),
            description: data[id]
          }));
        }
      }).catch(err => console.error(err));

      axios.get('/api/rosters').then(response => {
        const raw = response.data.data || response.data;
        if (Array.isArray(raw)) {
          this.rosters = raw.map(r => ({
            id: r.id,
            description: r.description || r.name || r
          }));
        } else if (typeof raw === 'object' && raw !== null) {
          this.rosters = Object.keys(raw).map(id => ({
            id: isNaN(parseInt(id)) ? id : parseInt(id),
            description: raw[id].description || raw[id]
          }));
        }
      }).catch(err => console.error(err));
    },
    loadEmployees() {
      this.updateComputedDates();
      if (!this.computedDates.date_from || !this.computedDates.date_to) {
        this.$message.warning('Por favor seleccione el mes y período quincenal');
        return;
      }

      this.loading = true;
      axios.get('/api/bonuses', {
        params: {
          date_from: this.computedDates.date_from,
          date_to: this.computedDates.date_to,
          functional_area_id: this.filters.functional_area_id,
          roster_id: this.filters.roster_id,
          filter: this.filters.filter
        }
      }).then(response => {
        const resData = response.data;
        const rawList = Array.isArray(resData) ? resData : (resData.data || []);
        this.cutoffExpired = resData && resData.cutoff_expired ? true : false;
        this.cutoffMessage = resData && resData.cutoff_message ? resData.cutoff_message : '';
        this.cutoffInfo = resData && resData.cutoff_info ? resData.cutoff_info : null;

        this.employees = rawList.map(emp => ({
          ...emp,
          amount: emp.amount !== null && emp.amount !== undefined ? parseFloat(emp.amount) : null,
          observation: emp.observation || ''
        }));
        this.loading = false;
      }).catch(err => {
        this.loading = false;
        this.$message.error('Error al cargar empleados');
        console.error(err);
      });
    },
    saveBonuses() {
      if (this.employees.length === 0 || this.cutoffExpired) return;

      this.saving = true;
      const payload = {
        date_from: this.computedDates.date_from,
        date_to: this.computedDates.date_to,
        bonuses: this.employees.map(emp => ({
          employee_id: emp.employee_id || emp.id,
          amount: emp.amount,
          observation: emp.observation
        }))
      };

      axios.post('/api/bonuses/bulk', payload).then(response => {
        this.saving = false;
        this.$message.success('Bonificaciones guardadas correctamente');
        this.loadEmployees();
      }).catch(err => {
        this.saving = false;
        let msg = err.response && err.response.data && err.response.data.message 
          ? err.response.data.message 
          : 'Ocurrió un error al guardar las bonificaciones';
        this.$message.error(msg);
        console.error(err);
      });
    },
    canEditBonus(row) {
      if (this.cutoffExpired) {
        return false;
      }
      if (!row || !row.bonus_id) return true;
      if (this.$auth) {
        const userProf = this.$auth.user && this.$auth.user.profile 
          ? (typeof this.$auth.user.profile === 'object' ? this.$auth.user.profile.profile : this.$auth.user.profile) 
          : null;
        if (['SADM', 'SuperAdmin', 'SuperAdministrador', 'SUPER_ADM'].includes(userProf)) return true;
        if (typeof this.$auth.can === 'function') {
          return this.$auth.can(['bonuses.edit', 'bonuses.edit_saved']);
        }
      }
      return false;
    }
  }
};
</script>

<style scoped>
.content-wrapper-inner {
  padding: 15px;
}
.period-info-banner {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #ffffff;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px rgba(30, 60, 114, 0.25);
  flex-wrap: wrap;
  gap: 12px;
}
.period-info-left {
  display: flex;
  flex-direction: column;
}
.period-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #cbd5e1;
  font-weight: 700;
  margin-bottom: 4px;
}
.period-dates {
  font-size: 20px;
  font-weight: 800;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.cutoff-badge {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  color: #ffffff;
}
.cutoff-expired-badge {
  background: #ef4444 !important;
  border-color: #dc2626 !important;
  color: #ffffff !important;
  font-weight: bold;
}
</style>
